// tabs/castable-spells.ts — client renderer for the PC sheet's Castable Spells tab.
//
// Lists and caster-dependent effects derive from the browser-held creature array.
//
// Rows keep the legacy classes (spells_known_tr spells_known_tr_<level> [_concentration] [_ritual] +
// data-spell-name) so the existing filter global fateTideSetCastableSpellFilter + the search box
// filter the TS rows unchanged. TomSelect-injected selects are initialized via initializeTomSelect().

import type { CachedCaster, CasterItemNode } from '../types';
import { generateAbilityButton, spellIsLevelUpcastable } from '../ability-button';
import { resolveNest, itemsCol } from '../../shared/array-types';
import { uuidOf, catalogNameById, userChoicePickerLabel } from './ability-row';
import { findFullNestedItem } from '../../object-card/find-cached-item';
import { num } from '../read-helpers';
import { sheetCastablePayload } from '../creature-selectors';
import { displayRefusal } from '../k5-runtime';
import { getSpellTimeShortLabel } from '../../shared/spell-casting-time';
import { rangeIcon, displayAoeCompact, displayDuration, chargeAbilityIcon } from '../../shared/spell-display';
import { sanguineDmgPickOptionIds, sanguineDamageTypeName } from '../sanguine-damage-type-pick';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { ftDispatchAbilityVariantClick } from './action-bar';
import { pageSummonInjectedCantrips } from '../../shared/summon-cantrips-browser';
import { paintCastableMarkup } from '../paint-markup';
import { replaceHydratedHtml } from '../../shared/replace-hydrated-html';

declare function initializeTomSelect(): void;

type CastableWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    useAbilityVariable1?: (abilityId: string, variable1: string) => unknown;
    rollDice?: (rollType: string, damageType: string, abilityId: string, dc: string, dice: string, die: string, mod: string, advDis: string) => unknown;
    fateTideSetCastableSpellFilter?: (mode: string, level: number | null, rootId: string) => unknown;
    openItemInfoValue?: (selectId: string) => unknown;
    changeCharges?: (abilityId: string, inputId: string) => unknown;
    changeInventoryCharges?: (sourceId: string, inputId: string) => unknown;
    handleAbilityChoiceChange?: (element: HTMLElement) => unknown;
};

interface Ft3dSpellViewState {
    mode: string;
    level: string;
    query: string;
    collapsed: string[];
}

const FT3D_SPELL_VIEW_KEY = 'ft3d_creature_spell_view_v1';
const FT3D_SPELL_VIEW_MAX_CASTERS = 24;
const EMPTY_CASTABLE_SPELLS = '<p class="text-muted p-3" role="status" data-ft-castable-empty="1">No castable spells.</p>';
const wiredSpellSearches = new WeakSet<HTMLInputElement>();

function readFt3dSpellViewStore(): Record<string, Ft3dSpellViewState> {
    try {
        const raw = localStorage.getItem(FT3D_SPELL_VIEW_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as Record<string, Ft3dSpellViewState> : {};
    } catch {
        try { localStorage.removeItem(FT3D_SPELL_VIEW_KEY); } catch { /* viewer-local nicety */ }
        return {};
    }
}

// The compact spell view (collapsible sections + persisted filter/search/collapse state) belongs to
// the FT3D floating sheet, whichever card kind it holds. `data-ft3d-sheet-compact` is the shared
// marker BOTH enhancers stamp on the actor window (my_campaign.php); the creature-only marker stays
// for the rules that genuinely are creature-specific.
function ft3dSpellRootFor(element: Element | null): HTMLElement | null {
    const root = element?.closest<HTMLElement>('.fate-tide-castable-spells-root') ?? null;
    return root?.closest('[data-ft3d-sheet-compact="1"]') ? root : null;
}

function ft3dSpellCasterId(root: HTMLElement): string {
    return root.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId ?? '';
}

function writeFt3dSpellView(root: HTMLElement): void {
    const casterId = ft3dSpellCasterId(root);
    if (!casterId) return;
    try {
        const store = readFt3dSpellViewStore();
        delete store[casterId];
        store[casterId] = {
            mode: root.dataset.filterMode || 'all',
            level: root.dataset.filterLevel || '',
            query: root.querySelector<HTMLInputElement>('.fate-tide-castable-spell-search')?.value || '',
            collapsed: Array.from(root.querySelectorAll<HTMLElement>('[data-ft-spell-section-collapsed="1"]'))
                .map(section => section.dataset.ftSpellSectionKey || '').filter(Boolean).slice(0, 12),
        };
        const ids = Object.keys(store);
        for (let index = 0; index < ids.length - FT3D_SPELL_VIEW_MAX_CASTERS; index += 1) {
            const stale = ids[index];
            if (stale) delete store[stale];
        }
        localStorage.setItem(FT3D_SPELL_VIEW_KEY, JSON.stringify(store));
    } catch { /* persistence must never block spell controls */ }
}

function setSpellSectionCollapsed(section: HTMLElement, collapsed: boolean): void {
    section.dataset.ftSpellSectionCollapsed = collapsed ? '1' : '0';
    const body = section.querySelector<HTMLElement>('[data-ft-spell-section-body]');
    const button = section.querySelector<HTMLElement>('[data-ft-spell-action="toggle-section"]');
    if (body) body.hidden = collapsed;
    button?.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
}

function restoreFt3dSpellView(root: HTMLElement): void {
    const casterId = ft3dSpellCasterId(root);
    const state = casterId ? readFt3dSpellViewStore()[casterId] : undefined;
    if (!state) return;
    const mode = ['all', 'level', 'concentration', 'ritual'].includes(state.mode) ? state.mode : 'all';
    const level = mode === 'level' && /^\d$/.test(state.level) ? state.level : '';
    root.dataset.filterMode = mode;
    root.dataset.filterLevel = level;
    const input = root.querySelector<HTMLInputElement>('.fate-tide-castable-spell-search');
    if (input) input.value = String(state.query || '').slice(0, 120);
    root.querySelectorAll<HTMLElement>('[data-ft-spell-filter-mode]').forEach(button => {
        const matches = button.dataset.ftSpellFilterMode === mode
            && (mode !== 'level' || button.dataset.ftSpellFilterLevel === level);
        button.classList.toggle('active', matches);
    });
    const collapsed = new Set(Array.isArray(state.collapsed) ? state.collapsed.map(String) : []);
    root.querySelectorAll<HTMLElement>('[data-ft-spell-section-key]').forEach(section => {
        setSpellSectionCollapsed(section, collapsed.has(section.dataset.ftSpellSectionKey || ''));
    });
    (window as CastableWindow).fateTideSetCastableSpellFilter?.(
        mode,
        mode === 'level' ? Number(level) : null,
        root.id,
    );
    input?.dispatchEvent(new Event('input', { bubbles: true }));
}

function spellAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-spell-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function spellInfoAttrs(itemId: string): string {
    return spellAttrs('open-info', { itemId });
}

function spellOpenValueAttrs(selectId: string): string {
    return spellAttrs('open-info-value', { selectId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-spell-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftSpellAction ?? '';
    const w = window as CastableWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.openItemInfo?.(itemId); }
    } else if (action === 'use-variable1') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const variable1 = el.dataset.ftVariable1 ?? '';
        const casterId = el.dataset.ftCasterId ?? el.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId ?? '';
        if (abilityId !== '') {
            event.preventDefault();
            const run = () => { w.useAbilityVariable1?.(abilityId, variable1); };
            if (!ftDispatchAbilityVariantClick(casterId, abilityId, run, variable1 === 'damage_only')) run();
        }
    } else if (action === 'roll-dice') {
        event.preventDefault();
        w.rollDice?.(
            el.dataset.ftRollTypeId ?? '',
            el.dataset.ftDamageTypeId ?? '',
            el.dataset.ftAbilityId ?? '',
            '',
            el.dataset.ftDice ?? '',
            el.dataset.ftDie ?? '',
            el.dataset.ftMod ?? '0',
            '0',
        );
    } else if (action === 'filter') {
        event.preventDefault();
        const mode = el.dataset.ftSpellFilterMode ?? '';
        const levelRaw = el.dataset.ftSpellFilterLevel;
        const level = levelRaw === undefined ? null : Number(levelRaw);
        w.fateTideSetCastableSpellFilter?.(mode, level, el.dataset.ftRootId ?? '');
        const root = ft3dSpellRootFor(el);
        if (root) writeFt3dSpellView(root);
    } else if (action === 'toggle-section') {
        event.preventDefault();
        const section = el.closest<HTMLElement>('[data-ft-spell-section-key]');
        if (!section) return;
        setSpellSectionCollapsed(section, section.dataset.ftSpellSectionCollapsed !== '1');
        const root = ft3dSpellRootFor(section);
        if (root) writeFt3dSpellView(root);
    }
});

document.addEventListener('input', (event) => {
    const input = event.target instanceof HTMLInputElement
        ? event.target.closest<HTMLInputElement>('.fate-tide-castable-spell-search') : null;
    const root = ft3dSpellRootFor(input);
    if (root) writeFt3dSpellView(root);
});

document.addEventListener('change', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-spell-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftSpellAction ?? '';
    const w = window as CastableWindow;
    if (action === 'open-info-value') {
        const selectId = el.dataset.ftSelectId ?? '';
        if (selectId !== '') w.openItemInfoValue?.(selectId);
    } else if (action === 'change-charges') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        const inputId = el.dataset.ftInputId ?? '';
        if (abilityId !== '' && inputId !== '') w.changeCharges?.(abilityId, inputId);
    } else if (action === 'change-inventory-charges') {
        const sourceId = el.dataset.ftSourceId ?? '';
        const inputId = el.dataset.ftInputId ?? '';
        if (sourceId !== '' && inputId !== '') w.changeInventoryCharges?.(sourceId, inputId);
    } else if (action === 'ability-choice-change') {
        // ability_choice branch pick — the shared template.js handler reads the row's
        // data-ft-ability-choice-parent dataset and paints the child into the data-ac-slot divs.
        w.handleAbilityChoiceChange?.(el);
    }
});

interface DmgType { id: string; name: string }
interface SpellEffect { dice: number; die: unknown; mod: unknown; roll_type_id: string; damage_only: boolean; icon_key: string; damage_types: DmgType[] }
interface Charges { current: unknown; max: unknown }
interface InvCharges { current: unknown; max: unknown; source_icon_type: string }
interface Choice { id: string; name: string }
interface SpellObj {
    nest: CasterItemNode;
    id: string; name: string; level: number;
    spell_type: string | null; source: string | null;
    class_icon_id: string; subclass_icon_id: string;
    concentration: boolean; ritual: boolean;
    charges: Charges | null; inventory_charges: InvCharges | null;
    effect: SpellEffect | null;
    summon_choices: Choice[] | null; shapeshift_choices: Choice[] | null;
    // ability_choice BRANCH list (Enhance Ability -> Bear's Endurance / Bull's Strength / ...). Non-empty
    // marks this row an ability_choice PARENT: it has no castable face of its own, so the button / cast
    // time / range / aoe / duration / hit_dc / effect cells render EMPTY and the branch <select> fills
    // them from the CHILD via handleAbilityChoiceChange.
    ability_choices: Choice[] | null;
}
/** Native selector rows carry the held definition and caster-dependent effect overrides.
 *  Injected cantrips may carry only a reference resolved against the held catalog. */
type RawSpellObj = Partial<SpellObj> & { nest?: unknown; id?: string; item_id?: string; effect?: Partial<SpellEffect> | null };
interface SpellsPayload {
    // Omitted lists are empty. Edit rights are recomputed for the viewer.
    spells_known?: RawSpellObj[]; curses_known?: RawSpellObj[];
    at_will_spell?: RawSpellObj[]; innate_spell_per_long_rest?: RawSpellObj[]; charged_spell?: RawSpellObj[];
    can_edit: boolean;
}
/** Hydrated payload — every row is a full SpellObj. */
interface HydratedPayload {
    spells_known?: SpellObj[]; curses_known?: SpellObj[];
    at_will_spell?: SpellObj[]; innate_spell_per_long_rest?: SpellObj[]; charged_spell?: SpellObj[];
    can_edit: boolean;
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

// Resolve definition fields and overlay the native selector's caster-dependent values.

function rollTypeIdByVn(vn: string): string {
    const c = window.ROLLTYPE_CATALOG;
    return String(c?.[vn]?.item_id || '');
}
// janus_hostile_paoe_roll_type_ids() — keep in sync with ability-effect-button.ts.
const HOSTILE_PAOE_ROLL_VNS = [
    'damage_roll', 'contesting_roll', 'damage_over_time_roll', 'weapon_melee_attack_roll',
    'weapon_ranged_attack_roll', 'spell_melee_attack_roll', 'spell_ranged_attack_roll',
    'extra_damage_taken_roll', 'mana_drain_roll',
];
function dmgTypeName(id: string): string {
    const c = window.DAMAGETYPE_CATALOG;
    if (c && typeof c === 'object') {
        for (const k of Object.keys(c)) {
            if (c[k] && c[k].item_id === id) return String(c[k].item_name || 'Damage Type');
        }
    }
    return 'Damage Type';
}

/** The spells_known/curses_known POINTER entry ({class?, subclass?, item_id}) for a spell id. */
function knownPointerEntry(caster: CachedCaster, spellId: string): { cls: string; sub: string } {
    const want = String(spellId).toUpperCase();
    const cc = caster.computed?.creature;
    const grab = (v: unknown): string => {
        if (typeof v === 'string') return v;
        if (v && typeof v === 'object') return String(itemsCol(v, 'item_id') ?? '');
        return '';
    };
    for (const listKey of ['spells_known', 'curses_known']) {
        const list = cc?.[listKey];
        if (!Array.isArray(list)) continue;
        for (const e of list) {
            if (!e) continue;
            if (typeof e === 'string') {
                if (e.toUpperCase() === want) return { cls: '', sub: '' };
                continue;
            }
            if (typeof e !== 'object') continue;
            const entry = recordOf(e);
            if (!entry) continue;
            const eid = String(entry.item_id ?? itemsCol(e, 'item_id') ?? '').toUpperCase();
            if (eid !== want) continue;
            return { cls: grab(entry.class), sub: grab(entry.subclass) };
        }
    }
    return { cls: '', sub: '' };
}

/** ROUND-9 #7 client twin of ft_derive_spell_known_class: the granting class when EXACTLY ONE
 *  of the PC's class providers lists the spell in its spell_known layer, '' otherwise. */
function deriveSpellClass(caster: CachedCaster, spellId: string): string {
    const pc = caster.player_characters;
    if (!pc || !spellId) return '';
    const want = String(spellId).toUpperCase();
    const matches = new Set<string>();
    for (let i = 1; i <= 20; i++) {
        const slot = pc[`class_${i}`];
        if (!slot) continue;
        let def: unknown = (typeof slot === 'string') ? resolveNest(slot, caster) : slot;
        if (!def || typeof def !== 'object') continue;
        // The spell_known LAYER lives on the class's full def. On the realiased encounter MEMBER
        // resolveNest's subtree walk misses (no own catalog), so resolve cross-root before reading
        // layers — without this a multiclass PC's spell-row class icon drops after realias.
        if (!recordOf(def)?.item_layer_types) {
            const cidRef = String((typeof slot === 'string' ? slot : itemsCol(slot, 'item_id')) ?? '');
            const full = cidRef ? findFullNestedItem(cidRef) : null;
            if (full) def = full;
        }
        const cid = String(itemsCol(def, 'item_id') ?? (typeof slot === 'string' ? slot : '')).toUpperCase();
        if (!cid) continue;
        for (const e of ftLayerEntries(def, 'spell_known')) {
            if (String(uuidOf(e)).toUpperCase() === want) { matches.add(cid); break; }
        }
    }
    return matches.size === 1 ? (Array.from(matches)[0] ?? '') : '';
}

/** Derive effect metadata from the held definition and apply the native dice/mod overrides. */
function hydrateEffect(raw: RawSpellObj, nest: CasterItemNode): SpellEffect | null {
    const effect = raw.effect;
    if (!ftLayerHas(nest, 'die')) return null;
    const rollType = String(ftLayerValue(nest, 'roll_type') || rollTypeIdByVn('damage_roll'));
    const dtIds: string[] = ftLayerEntries(nest, 'damage_type').map(uuidOf).filter((x: string) => x !== '');
    let iconKey = '';
    if (dtIds.length === 1) iconKey = dtIds[0] ?? '';
    else if (rollType === rollTypeIdByVn('healing_roll')) iconKey = 'healing';
    else if (rollType === rollTypeIdByVn('temporary_vitality_roll') || rollType === rollTypeIdByVn('temporary_vitality_regeneration_roll')) iconKey = 'v_temp';
    const hostile = new Set(HOSTILE_PAOE_ROLL_VNS.map(rollTypeIdByVn).filter(Boolean));
    return {
        dice: (effect?.dice ?? num(ftLayerValue(nest, 'dice'), 0)) as number,
        die: ftLayerValue(nest, 'die'),
        mod: effect?.mod ?? num(ftLayerValue(nest, 'mod'), 0),
        roll_type_id: rollType,
        damage_only: hostile.has(rollType),
        icon_key: iconKey,
        damage_types: dtIds.map((id) => ({ id, name: dmgTypeName(id) })),
    };
}

function hydrateCastableRow(raw: RawSpellObj, caster: CachedCaster): SpellObj {
    // The selector names the catalog ref item_id; the internal row uses id.
    const itemId = String(raw.item_id ?? raw.id ?? '');
    // ROUND-5 #4 / ROUND-10: a BAKED row's `nest` is a bare-UUID catalog ref — or absent
    // entirely once it equality-elided against item_id (v31) — resolve against the caster blob
    // before the cells (and the derivations below) read its layers.
    let nest = recordOf(raw.nest)?.item_layer_types
        ? recordOf(raw.nest)
        : recordOf(resolveNest(raw.nest ?? itemId, caster));
    // Realias path: the encounter-MEMBER blob carries NO own catalog (it unions up to the
    // encounter ROOT), so resolveNest's caster-subtree walk + fullNodeById both miss and hand
    // back the bare UUID ref. Resolve the spell's full def across ALL cache roots (incl. the
    // encounter root catalog) — the same findFullNestedItem fallback ability-row.ts uses, which
    // is why the Abilities tab survives realias but spells didn't. Without this, the moment
    // ftRealiasCastersFromEncounterBlob repoints the alias the spell name goes blank and the
    // rune face renders as "?" (generateAbilityButton can't read layers off a bare string).
    if (!nest?.item_layer_types) {
        const full = findFullNestedItem(itemId);
        if (full) nest = recordOf(full);
    }
    const hydratedNest: CasterItemNode = nest ?? { items: { item_id: itemId } };
    const ptr = knownPointerEntry(caster, itemId);
    return {
        nest: hydratedNest,
        id: itemId,
        // Baked rows drop the name (equality-elided); recover it from the resolved def, then fall
        // back to the cross-root catalog identity (last resort when only an identity row exists).
        // item_name is persisted HTML-ENCODED ("Curse of Death&#039;s Inevitability") on every one of
        // those paths — decode ONCE here, the single hydration funnel, so nameCell/rowAttrs' escapeHtml
        // stays the sole encoder instead of re-encoding the entity's & into a visible "&#039;".
        name: decodeHtmlEntitiesForPlainText(raw.name ?? (String(itemsCol(hydratedNest, 'item_name') ?? '') || catalogNameById(itemId))),
        level: raw.level ?? num(ftLayerValue(hydratedNest, 'spell_level'), 0),
        spell_type: raw.spell_type ?? null,
        source: raw.source ?? null,
        class_icon_id: raw.class_icon_id ?? (ptr.cls || deriveSpellClass(caster, itemId)),
        subclass_icon_id: raw.subclass_icon_id ?? ptr.sub,
        concentration: raw.concentration ?? ftLayerHas(hydratedNest, 'spell_concentration'),
        ritual: raw.ritual ?? ftLayerHas(hydratedNest, 'spell_ritual'),
        charges: raw.charges ?? null,
        inventory_charges: raw.inventory_charges ?? null,
        effect: hydrateEffect(raw, hydratedNest),
        summon_choices: raw.summon_choices ?? null,
        shapeshift_choices: raw.shapeshift_choices ?? null,
        ability_choices: raw.ability_choices ?? deriveAbilityChoices(hydratedNest),
    };
}

/** Resolve a choice parent's option labels from the held definition and name catalog. */
function deriveAbilityChoices(nest: CasterItemNode): Choice[] | null {
    const out: Choice[] = [];
    for (const entry of ftLayerEntries(nest, 'ability_choice')) {
        const id = uuidOf(entry);
        if (id === '') continue;
        let name = String(itemsCol(recordOf(entry) ?? {}, 'item_name') ?? '');
        if (name === '') name = catalogNameById(id);
        if (name === '' && typeof ftItemNameById === 'function') name = String(ftItemNameById(id) ?? '');
        out.push({ id, name: decodeHtmlEntitiesForPlainText(name || id) });
    }
    return out.length > 0 ? out : null;
}

/** An ability_choice PARENT — the row is a branch picker, not a castable spell. */
function isChoiceParent(o: SpellObj): boolean {
    return !!o.ability_choices && o.ability_choices.length > 0;
}

/** Placed creatures use their encounter identity; standalone PCs use their item identity. */
function casterRequestId(caster: CachedCaster): string {
    const ec = caster?.encounter_creatures;
    const ecId = (ec && !Array.isArray(ec)) ? ec.encounter_creature_id : undefined;
    const unwrap = (v: unknown): string => String((Array.isArray(v) ? v[0] : v) ?? '');
    return unwrap(ecId) || String(itemsCol(caster, 'item_id') ?? '');
}

const sanitizeId = (id: string): string => String(id).replace(/[^A-Za-z0-9_-]/g, '_');

// House spyglass + clear (X) trailing icons — TS twin of includes/search_inline_trailing_icons_inc.php
// (the same markup catalog-list and the item-type search fields use). The wrapper must be
// .position-relative.ft-inline-search-root and the input must reserve room with pe-5.
const SEARCH_TRAILING_ICONS =
    '<span class="ft-inline-search-trailing position-absolute top-50 end-0 translate-middle-y me-2 d-flex align-items-center justify-content-center">'
    + '<button type="button" class="ft-inline-search-clear-btn d-none" data-ft-inline-search-role="clear" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="Clear" aria-label="Clear search">'
    + '<span aria-hidden="true" style="line-height:0;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false" class="ft-inline-search-trailing-svg"><path d="M18 6L6 18M6 6l12 12"/></svg></span></button>'
    + '<span class="ft-inline-search-glyph" data-ft-inline-search-role="glyph" aria-hidden="true" style="line-height:0;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false" class="ft-inline-search-trailing-svg"><circle cx="11" cy="11" r="7"/><path d="M16 16l4.5 4.5"/></svg></span>'
    + '</span>';

function spellTimeDisplay(nest: CasterItemNode): string {
    return getSpellTimeShortLabel(String(ftLayerValue(nest, 'spell_casting_time') ?? ''));
}
// ── cells ──────────────────────────────────────────────────────────────────
function castCell(o: SpellObj, caster: CachedCaster, canEdit: boolean): string {
    // A choice PARENT has no castable face — both slots stay empty until a branch is picked and
    // The browser branch renderer fills them with the CHILD's button and casting time.
    if (isChoiceParent(o)) return `<div data-ac-slot="button"></div><div data-ac-slot="cast_time"></div>`;
    const btn = generateAbilityButton(o.nest, caster, { spellType: o.spell_type, source: o.source, buttonDisabled: !canEdit });
    return `<div data-ac-slot="button">${btn}</div><div data-ac-slot="cast_time">${escapeHtml(spellTimeDisplay(o.nest))}</div>`;
}
function upcastCell(o: SpellObj, canEdit: boolean): string {
    // Cantrips carve out of the level picker (owner 2026-07-24) — they cost no mana, so a level
    // box would buy their upcast dice/duration free. They scale via spell_scalable_cantrip.
    if (!spellIsLevelUpcastable(o.nest)) return '';
    const lvl = String(ftLayerValue(o.nest, 'spell_level') ?? 0);
    return `<input id="input_${escapeHtml(o.id)}_spell_upcastable" class="form-control form-control-sm ft-sheet-charges-input" type="number" min="${escapeHtml(lvl)}" max="" value="${escapeHtml(lvl)}"${canEdit ? '' : ' disabled'}>`;
}
function ritualCell(o: SpellObj, caster: CachedCaster, canEdit: boolean): string {
    if (!o.ritual) return '';
    // A choice parent's ritual button would cast the PARENT, which carries no effect layers and rolls
    // nothing — the branch endpoint only replaces the normal cast button, not this one. Hide it rather
    // than offer a cast that silently does nothing. (Codex review 2026-07-28.)
    if (isChoiceParent(o)) return '';
    return `<button type="button" class="btn btn-info btn-sm"${canEdit ? ` ${spellAttrs('use-variable1', { abilityId: o.id, variable1: 'spell_ritual', casterId: casterRequestId(caster) })}` : ' disabled'}>${iconMap('cast_ritual_spell')}</button>`;
}
/** The ability_choice branch picker: `-- SELECT OPTION --` + one option per branch, under the canonical
 *  `input_<parentId>_ability_choice_branch` id the legacy row used. Change routes to the shared
 *  window.handleAbilityChoiceChange (template.js), which calls the browser branch renderer
 *  and paints the CHILD's button / name / effect / spell columns into this row's data-ac-slot divs. */
function branchSelect(o: SpellObj, canEdit: boolean): string {
    let opts = '<option value="">-- SELECT OPTION --</option>';
    for (const c of o.ability_choices ?? []) {
        opts += `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`;
    }
    return `<div class="mb-2"><select class="ts-select form-select form-select-sm" id="input_${escapeHtml(o.id)}_ability_choice_branch" ${spellAttrs('ability-choice-change')}${canEdit ? '' : ' disabled'}>${opts}</select></div>`;
}
/** The branch picker as it sits in the EFFECT column, ABOVE the data-ac-slot="effect" div — never
 *  inside it, because handleAbilityChoiceChange replaces that div's innerHTML wholesale and would
 *  blow the select away on the first pick. '' for a non-choice row. */
function branchSelectCell(o: SpellObj, canEdit: boolean): string {
    return isChoiceParent(o) ? branchSelect(o, canEdit) : '';
}

function spellNameButton(o: SpellObj, muted = false): string {
    return `<button type="button" class="btn border-0 bg-transparent p-0 text-start ${muted ? 'text-secondary small' : ''} ft-spell-name-button"`
        + ` ${spellInfoAttrs(o.id)}>${escapeHtml(o.name)}</button>`;
}

function nameCell(o: SpellObj, canEdit = true, withClassBadge = false, className = ''): string {
    // Class/subclass provenance: the subclass already implies the class, so show the subclass when
    // present and fall back to the class otherwise — never both (drops the redundant double glyph).
    const classLine = o.subclass_icon_id ? iconMap(o.subclass_icon_id) : (o.class_icon_id ? iconMap(o.class_icon_id) : '');
    const badge = withClassBadge && className ? `<div><span class="badge bg-light text-dark">${escapeHtml(className)}</span></div>` : '';
    if (isChoiceParent(o)) {
        // The branch picker itself lives in the EFFECT column (owner 2026-07-28) — picking a branch IS
        // choosing the effect, and it keeps the Name column reading as a name. Here: the resolved
        // child's name block, then the PARENT's name as a secondary info link.
        return `<div class="ft-spell-name-wrap"><div data-ac-slot="name" class="mb-1"></div>`
            + spellNameButton(o, true)
            + `<div class="mt-1">${classLine}</div>${badge}</div>`;
    }
    // No rune glyph here: the cast button in the first column already renders the spell's rune face
    // (generateAbilityButton → runeMountHtml), so repeating it beside the name showed the symbol twice.
    return `<div class="ft-spell-name-wrap"><div class="d-flex align-items-center">${spellNameButton(o)}</div>`
        + `<div>${classLine}</div>${badge}</div>`;
}
function rangeCell(o: SpellObj): string {
    return rangeIcon(o.nest);
}
function aoeCell(o: SpellObj): string {
    return displayAoeCompact(o.nest) || '—';
}
function durationCell(o: SpellObj): string {
    let out = displayDuration(o.nest, true);
    if (o.concentration) out += `<br />${iconMap('concentration')}`;
    return out;
}
function rollCell(o: SpellObj, asButton = false): string {
    const contestee = ftLayerValue(o.nest, 'skill_roll_contestee');
    const rt = ftLayerValue(o.nest, 'roll_type');
    const id = contestee ? String(contestee) : (rt ? String(rt) : '');
    if (!id) return '';
    return asButton ? `<button class='btn btn-warning btn-sm text-dark border border-dark'>${iconMap(id)}</button>` : iconMap(id);
}
function specialChargesCell(o: SpellObj): string {
    let out = '';
    const consumes = ftLayerValue(o.nest, 'consumes_ability_charges');
    if (consumes !== null && consumes !== undefined) {
        const qty = ftLayerValue(o.nest, 'consumes_charges_qty');
        if (qty !== null && qty !== undefined) out += `<span class="badge bg-dark border me-1">${escapeHtml(String(qty))}</span>`;
        out += chargeAbilityIcon(consumes);
    }
    const adds = ftLayerValue(o.nest, 'add_charges_to_ability');
    if (adds !== null && adds !== undefined) {
        const qty = ftLayerValue(o.nest, 'add_charges_qty');
        if (qty !== null && qty !== undefined) out += `<span class="badge bg-dark border me-1">${escapeHtml(String(qty))}</span>`;
        out += chargeAbilityIcon(adds);
    }
    return out;
}
function choiceSelect(choices: Choice[] | null, selId: string, canEdit: boolean): string {
    if (!choices || choices.length === 0) return '';
    // The EMPTY option's text is the placeholder — Tom Select harvests it and overwrites the
    // setting tom-select-init passes, so naming it here is the only thing that sticks.
    const label = userChoicePickerLabel(selId);
    let opts = `<option value="">${escapeHtml(label)}</option>`;
    for (const c of choices) opts += `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`;
    // Ungated + compact for the same reasons as ability-row.ts choiceSelect: open-info-value is a
    // READ, and ts-auto-width must sit on the wrapper for the descendant CSS to match.
    return `<div class="ts-auto-width mt-1"><select class="ts-select ts-auto-width" id="${escapeHtml(selId)}"`
        + ` placeholder="${escapeHtml(label)}" title="${escapeHtml('Choose ' + label)}"`
        + ` ${spellOpenValueAttrs(selId)}>${opts}</select></div>`;
}
/** The icon-only damage-type <select>. `sanguine` marks the Elemental/Transmuted variant so the AOE
 *  confirm can find it by attribute — the legacy multi-type select must NOT carry that stamp (its
 *  value already rides variable_1 as the button's input_id_reference). */
function damageTypeSelect(types: DmgType[], id: string, canEdit: boolean, sanguine = false): string {
    let opts = '';
    for (const t of types) {
        const icon = iconMap(t.id, 25, 25, false);
        opts += `<option value="${escapeHtml(t.id)}" data-icon="${escapeHtml(icon)}" title="${escapeHtml(t.name)}"></option>`;
    }
    const sangAttr = sanguine ? ` data-ft-sanguine-dmg-for="${escapeHtml(String(id).toUpperCase())}"` : '';
    const title = sanguine ? 'Sanguine: choose this cast&#39;s damage type' : 'Choose Damage Type';
    return `<select class="ts-select ts-auto-width icon-only-damage-select" id="input_${escapeHtml(id)}_user_choice_1_damage_type" title="${title}"${canEdit ? '' : ' disabled'}${sangAttr}>${opts}</select>`;
}
/** A cast whose use_ability_variable_2 is already spoken for by its SOURCE row (charged / innate
 *  item spells) — the Sanguine pick can't share that slot. Mirrors the action bar's itemSourced gate. */
function isItemSourcedSpell(o: SpellObj): boolean {
    return !!o.source;
}
function effectCell(o: SpellObj, caster: CachedCaster, canEdit: boolean): string {
    // Choice parent: the effect belongs to whichever branch is picked — the slot stays empty and
    // handleAbilityChoiceChange fills it with render_ability_effect_button() for the CHILD.
    if (isChoiceParent(o)) return '';
    let html = choiceSelect(o.summon_choices, `input_${o.id}_user_choice_1_summon`, canEdit)
        + choiceSelect(o.shapeshift_choices, `input_${o.id}_user_choice_1_shapeshift`, canEdit);
    const e = o.effect;
    if (!e) return html;
    const types = e.damage_types ?? [];
    const multi = types.length > 1;
    const primary = types[0]?.id ?? '';
    // Sanguine Elemental/Transmuted: an armed next_spell_damage_type_choice buff lets the player retype
    // THIS damaging spell even though it authored ≤1 type. Rendered under the SAME canonical select id
    // as the legacy multi-type picker, so the cast button and FT3D placement read it
    // through the existing use_ability_variable_2 plumbing. Server re-authorizes the pick.
    const sangIds = multi ? [] : sanguineDmgPickOptionIds(caster, o.nest, isItemSourcedSpell(o));
    if (multi || sangIds.length > 0) html += '<div class="d-flex align-items-center gap-2">';
    const actionAttrs = e.damage_only
        ? spellAttrs('use-variable1', { abilityId: o.id, variable1: 'damage_only', casterId: casterRequestId(caster) })
        : spellAttrs('roll-dice', {
            rollTypeId: e.roll_type_id,
            damageTypeId: primary,
            abilityId: o.id,
            dice: String(e.dice),
            die: String(e.die),
            mod: String(e.mod ?? 0),
        });
    let btn = `<button type="button" class="btn btn-sm btn-danger border border-dark"${canEdit ? ` ${actionAttrs}` : ' disabled'}>`;
    btn += `${escapeHtml(String(e.dice))}d${escapeHtml(String(e.die))}`;
    if (e.mod !== null && e.mod !== undefined && e.mod !== 0 && e.mod !== '0' && e.mod !== '') btn += ` + ${escapeHtml(String(e.mod))}`;
    if (e.icon_key) btn += ' ' + iconMap(e.icon_key);
    btn += '</button>';
    html += btn;
    if (multi) html += damageTypeSelect(types, o.id, canEdit) + '</div>';
    else if (sangIds.length > 0) {
        const sangTypes = sangIds.map((id) => ({ id, name: sanguineDamageTypeName(id) }));
        html += damageTypeSelect(sangTypes, o.id, canEdit, true) + '</div>';
    }
    return html;
}

function rowClasses(o: SpellObj): string {
    return `spells_known_tr spells_known_tr_${o.level}${o.concentration ? ' spells_known_tr_concentration' : ''}${o.ritual ? ' spells_known_tr_ritual' : ''}${isChoiceParent(o) ? ' fate-tide-ability-choice-row' : ''}`;
}
/** Row attrs. A choice parent additionally carries the dataset handleAbilityChoiceChange reads to build
 *  its POST (parent id, caster, and the spell_type/source markers so the CHILD's button keeps the
 *  at-will / innate / charged accounting). */
function rowAttrs(o: SpellObj, caster: CachedCaster): string {
    let attrs = `class="${rowClasses(o)}" data-ft-spell-id="${escapeHtml(o.id)}" data-ft-spell-source="${escapeHtml(o.source ?? '')}" data-spell-name="${escapeHtml(o.name.toLowerCase())}"`;
    if (isChoiceParent(o)) {
        attrs += ` data-ft-ability-choice-parent="${escapeHtml(o.id)}"`
            + ` data-ft-caster-item-id="${escapeHtml(casterRequestId(caster))}"`
            + ` data-ft-ac-spell-type="${escapeHtml(o.spell_type ?? '')}"`
            + ` data-ft-ac-source="${escapeHtml(o.source ?? '')}"`
            + ` data-ft-ac-consume-nested="0"`
            + ` data-ft-ac-extend-spell-columns="1"`;
    }
    return attrs;
}
/** Supporting spell columns (range / aoe / duration / hit_dc): empty on a choice parent — the picked
 *  branch's values arrive through the same data-ac-slot divs. */
const acCell = (o: SpellObj, html: string): string => (isChoiceParent(o) ? '' : html);
const spellValue = (html: string): string => html.trim() !== ''
    ? html : '<span class="ft-spell-empty" aria-label="Not available">—</span>';

// ── leveled / curse row (spell_known_tr) ──
function leveledRow(o: SpellObj, caster: CachedCaster, canEdit: boolean): string {
    const cast = spellValue(castCell(o, caster, canEdit));
    const upcast = spellValue(upcastCell(o, canEdit));
    const ritual = spellValue(ritualCell(o, caster, canEdit));
    const charges = spellValue(specialChargesCell(o));
    const range = spellValue(acCell(o, rangeCell(o)));
    const aoe = spellValue(acCell(o, aoeCell(o)));
    const duration = spellValue(acCell(o, durationCell(o)));
    const roll = spellValue(acCell(o, rollCell(o)));
    const branch = branchSelectCell(o, canEdit);
    const effect = spellValue(effectCell(o, caster, canEdit));
    return `<tr ${rowAttrs(o, caster)}>
        <td class="text-center text-nowrap align-middle ft-spell-cell ft-spell-cell--cast" data-ft-spell-label="Cast">${cast}</td>
        <td class="text-center ft-sheet-charges-cell ft-spell-cell ft-spell-cell--upcast" data-ft-spell-label="Upcast">${upcast}</td>
        <td class="ft-spell-cell ft-spell-cell--ritual" data-ft-spell-label="Ritual">${ritual}</td>
        <td class="ft-spell-cell ft-spell-cell--name" data-ft-spell-label="Name">${nameCell(o, canEdit)}</td>
        <td class="text-center ft-sheet-charges-cell ft-spell-cell ft-spell-cell--charges" data-ft-spell-label="Charges">${charges}</td>
        <td class="text-center ft-spell-cell ft-spell-cell--range" data-ft-spell-label="Range"><div data-ac-slot="range">${range}</div></td>
        <td class="text-center text-nowrap ft-spell-cell ft-spell-cell--aoe" data-ft-spell-label="AOE"><div data-ac-slot="aoe">${aoe}</div></td>
        <td class="text-center text-nowrap ft-spell-cell ft-spell-cell--duration" data-ft-spell-label="Duration"><div data-ac-slot="duration">${duration}</div></td>
        <td class="text-center align-middle ft-spell-cell ft-spell-cell--roll" data-ft-spell-label="Roll"><div data-ac-slot="hit_dc">${roll}</div></td>
        <td class="ft-spell-cell ft-spell-cell--effect" data-ft-spell-label="Effect">${branch}<div data-ac-slot="effect">${effect}</div></td>
    </tr>`;
}

function leveledTable(title: string, divCls: string, rows: string, sectionKey: string): string {
    return `<div class="spells_known_div ${divCls}" data-ft-spell-section-key="${escapeHtml(sectionKey)}" data-ft-spell-section-collapsed="0">
        <div><div><h5 class="mt-1 mb-0 bg-dark text-light bg-gradient rounded-top text-left"><button type="button" class="ft-spell-section-toggle" aria-expanded="true" ${spellAttrs('toggle-section')}><span>${title}</span><span class="ft-spell-section-toggle__chevron" aria-hidden="true">⌄</span></button></h5></div></div>
        <div class="table-responsive-xxl pl-1 pr-1 pb-2 pt-0 mb-3" data-ft-spell-section-body><table class="table table-hover table-sm ft-castable-spell-table ft-castable-spell-table--leveled">
            <thead><tr>
                <th class='text-center ft-spell-cell ft-spell-cell--cast' style='width:55px;'>${iconMap('spell')}</th>
                <th class='text-center ft-sheet-charges-cell ft-spell-cell ft-spell-cell--upcast' style='width:55px;min-width:55px;'>${iconMap('spell_upcastable')}</th>
                <th class='text-center ft-spell-cell ft-spell-cell--ritual' style='width:55px;'>${iconMap('spell_ritual')}</th>
                <th class='col-3 ft-spell-cell ft-spell-cell--name'>Name</th>
                <th class='text-center ft-sheet-charges-cell ft-spell-cell ft-spell-cell--charges' style='width:70px;'>${iconMap('charges')}</th>
                <th class='text-center ft-spell-cell ft-spell-cell--range' style='width:55px;'>${iconMap('range')}</th>
                <th class='text-center ft-spell-cell ft-spell-cell--aoe' style='width:83px;'>AOE</th>
                <th class='text-center ft-spell-cell ft-spell-cell--duration' style='width:83px;'>${iconMap('duration')}</th>
                <th class='text-center ft-spell-cell ft-spell-cell--roll' style='width:83px;'>${iconMap('roll_type')}</th>
                <th class='text-left ft-spell-cell ft-spell-cell--effect'>Effect</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table></div>
    </div>`;
}

function ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
    return n + (s[(v - 20) % 10] ?? s[v] ?? 'th');
}

function buildLeveled(payload: HydratedPayload, caster: CachedCaster, maxLevel: number): string {
    let html = '';
    // Curses
    const curses = payload.curses_known ?? [];
    if (curses.length > 0) {
        const rows = curses.map((o) => leveledRow(o, caster, payload.can_edit)).join('');
        html += leveledTable(`${iconMap('curses')} Curses`, 'spells_known_div_curse', rows, 'curse');
    }
    // Leveled spells 0..max
    const known = payload.spells_known ?? [];
    for (let x = 0; x <= 9; x++) {
        if (maxLevel < x) continue;
        const atLevel = known.filter((o) => o.level === x);
        if (atLevel.length === 0) continue; // empty tier → skip the whole section (header + table)
        const rows = atLevel.map((o) => leveledRow(o, caster, payload.can_edit)).join('');
        const title = x === 0 ? 'Cantrips' : `${ordinal(x)} Level Spells`;
        html += leveledTable(title, `spells_known_div_${x}`, rows, String(x));
    }
    return html;
}

// ── innate / charged row (spell_known_charges_tr) ──
function innateChargedRow(o: SpellObj, caster: CachedCaster, canEdit: boolean): string {
    const isCharged = o.spell_type === 'charged_spell';
    const isInnatePLR = o.spell_type === 'innate_spell_per_long_rest';

    let equipmentCell = '';
    if (isCharged) {
        const icon = o.inventory_charges?.source_icon_type ? iconMap(o.inventory_charges.source_icon_type) : '';
        equipmentCell = `<td class="text-center align-middle nowrap ft-spell-cell ft-spell-cell--source" data-ft-spell-label="Source"><span role="button" ${spellInfoAttrs(o.source ?? '')}>${icon}</span></td>`;
    }
    let chargesCell = '';
    if (isInnatePLR || isCharged) {
        let inner = '';
        if (isInnatePLR && o.charges) {
            const inputId = `${o.id}_innate_spell_per_long_rest_charges`;
            inner = `<input id="${escapeHtml(inputId)}" class="form-control form-control-sm ft-sheet-charges-input" type="number" min="0" max="${escapeHtml(String(o.charges.max ?? ''))}" value="${escapeHtml(String(o.charges.current ?? ''))}" ${spellAttrs('change-charges', { abilityId: o.id, inputId })}${canEdit ? '' : ' disabled'}>`;
        } else if (isCharged) {
            inner = `<h5><span class='badge bg-small bg-dark border'>${escapeHtml(String(ftLayerValue(o.nest, 'spell_level') ?? 1))}</span></h5>`;
        }
        chargesCell = `<td class="text-center align-middle text-nowrap ft-sheet-charges-cell ft-spell-cell ft-spell-cell--charges" data-ft-spell-label="${isCharged ? 'Level' : 'Charges'}">${inner}</td>`;
    }
    let invChargesCell = '';
    if (isCharged && o.inventory_charges) {
        const inputId = `${o.id}_${o.source}_inventory_current_item_charges`;
        invChargesCell = `<td class="text-center align-middle text-nowrap ft-sheet-charges-cell ft-spell-cell ft-spell-cell--item-charges" data-ft-spell-label="Item Charges"><input id="${escapeHtml(inputId)}" class="form-control form-control-sm ft-sheet-charges-input" type="number" min="0" max="${escapeHtml(String(o.inventory_charges.max ?? ''))}" value="${escapeHtml(String(o.inventory_charges.current ?? ''))}" ${spellAttrs('change-inventory-charges', { sourceId: o.source ?? '', inputId })}${canEdit ? '' : ' disabled'}></td>`;
    }

    return `<tr ${rowAttrs(o, caster)}>
        <td class="text-center align-middle nowrap ft-spell-cell ft-spell-cell--cast" data-ft-spell-label="Cast">${castCell(o, caster, canEdit)}</td>
        ${equipmentCell}${chargesCell}${invChargesCell}
        <td class="ft-spell-cell ft-spell-cell--name" data-ft-spell-label="Name">${nameCell(o, canEdit)}</td>
        <td class="text-center ft-spell-cell ft-spell-cell--range" data-ft-spell-label="Range"><div data-ac-slot="range">${acCell(o, rangeCell(o))}</div></td>
        <td class="text-center text-nowrap ft-spell-cell ft-spell-cell--aoe" data-ft-spell-label="AOE"><div data-ac-slot="aoe">${acCell(o, aoeCell(o))}</div></td>
        <td class="text-center text-nowrap ft-spell-cell ft-spell-cell--duration" data-ft-spell-label="Duration"><div data-ac-slot="duration">${acCell(o, durationCell(o))}</div></td>
        <td class="text-center ft-spell-cell ft-spell-cell--roll" data-ft-spell-label="Roll"><div data-ac-slot="hit_dc">${acCell(o, rollCell(o, true))}</div></td>
        <td class="ft-spell-cell ft-spell-cell--effect" data-ft-spell-label="Effect">${branchSelectCell(o, canEdit)}<div data-ac-slot="effect">${effectCell(o, caster, canEdit)}</div></td>
    </tr>`;
}

function innateTable(type: string, label: string, rows: string, withCharges: boolean): string {
    const chargesTh = withCharges ? `<th class='text-center ft-sheet-charges-cell' style='width:55px;'>${iconMap('charges_remaining')}</th>` : '';
    return `<div class="spells_known_div spells_known_div_${type}" data-ft-spell-section-key="${type}">
        <div><div><h5 class="mt-1 mb-0 pt-2 pb-2 pl-2 bg-dark text-light bg-gradient rounded-top text-left">${label}</h5></div></div>
        <div class="table-responsive-xxl pl-1 pr-1 pb-2 pt-0 mb-3"><table class="table table-hover table-sm ft-castable-spell-table ft-castable-spell-table--aux ft-castable-spell-table--${type}">
            <thead><tr>
                <th class='text-center' style='width:55px;'>${iconMap('spell')}</th>${chargesTh}
                <th class='col-3'>Name</th>
                <th class='text-center' style='width:55px;'>${iconMap('range')}</th>
                <th class='text-center' style='width:83px;'>AOE</th>
                <th class='text-center' style='width:83px;'>${iconMap('duration')}</th>
                <th class='text-center' style='width:83px;'>${iconMap('roll_type')}</th>
                <th class='text-left'>Effect</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table></div>
    </div>`;
}
function chargedTable(rows: string): string {
    return `<div class="spells_known_div spells_known_div_charged_spell" data-ft-spell-section-key="charged_spell">
        <div><div><h5 class="mt-1 mb-0 pt-2 pb-2 pl-2 bg-dark text-light bg-gradient rounded-top text-left">Charged Spell(s)</h5></div></div>
        <div class="table-responsive-xxl pl-1 pr-1 pb-2 pt-0 mb-3"><table class="table table-hover table-sm ft-castable-spell-table ft-castable-spell-table--aux ft-castable-spell-table--charged_spell">
            <thead><tr>
                <th class='text-center' style='width:55px;'>${iconMap('spell')}</th>
                <th class='text-center' style='width:55px;'>${iconMap('inventory')}</th>
                <th class='text-center ft-sheet-charges-cell' style='width:55px;'>${iconMap('charges')}</th>
                <th class='text-center ft-sheet-charges-cell' style='width:55px;'>${iconMap('item_charges')}</th>
                <th class='col-3'>Name</th>
                <th class='text-center' style='width:55px;'>${iconMap('range')}</th>
                <th class='text-center' style='width:83px;'>AOE</th>
                <th class='text-center' style='width:83px;'>${iconMap('duration')}</th>
                <th class='text-center' style='width:83px;'>${iconMap('roll_type')}</th>
                <th class='text-left'>Effect</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table></div>
    </div>`;
}

function buildInnateCharged(payload: HydratedPayload, caster: CachedCaster): string {
    let html = '';
    const ce = payload.can_edit;
    const atWill = payload.at_will_spell ?? [];
    if (atWill.length > 0) {
        html += innateTable('at_will_spell', 'At Will Spell(s)', atWill.map((o) => innateChargedRow(o, caster, ce)).join(''), false);
    }
    const innatePLR = payload.innate_spell_per_long_rest ?? [];
    if (innatePLR.length > 0) {
        html += innateTable('innate_spell_per_long_rest', 'Innate Spell Per Long Rest(s)', innatePLR.map((o) => innateChargedRow(o, caster, ce)).join(''), true);
    }
    const charged = payload.charged_spell ?? [];
    if (charged.length > 0) {
        html += chargedTable(charged.map((o) => innateChargedRow(o, caster, ce)).join(''));
    }
    return html;
}

// ── nav pills + search ──
function navPills(maxLevel: number, rootId: string): string {
    if (maxLevel < 0) return '';
    // Each pill carries data-ft-spell-filter-mode/-level so the shared fateTideSetCastableSpellFilter
    // can move the `active` highlight to the chosen pill (these pills don't use data-bs-toggle, so
    // Bootstrap won't manage active state for us).
    const pill = (label: string, mode: string, level: number | null, active = false): string => {
        const lvlAttr = level !== null ? ` data-ft-spell-filter-level="${level}"` : '';
        const rootAttr = ` data-ft-root-id="${escapeHtml(rootId)}"`;
        return `<li class="nav-item p-1" role="presentation"><button class="nav-link border${active ? ' active' : ''}" type="button" data-ft-spell-filter-mode="${mode}"${lvlAttr}${rootAttr} ${spellAttrs('filter')}>${label}</button></li>`;
    };
    let pills = pill('All', 'all', null, true);
    for (let x = 0; x <= 9; x++) {
        if (maxLevel >= x) pills += pill(String(x), 'level', x);
    }
    pills += pill(iconMap('concentration'), 'concentration', null);
    pills += pill(iconMap('spell_ritual'), 'ritual', null);
    return `<ul class="nav nav-pills flex-wrap align-items-center mb-0" role="tablist">${pills}</ul>`;
}

/** Lay out native selector rows. Edit rights are overridden per viewer. */
function paintCastableSpells(raw: SpellsPayload, caster: CachedCaster, casterId: string, container: HTMLElement): void {
    // can_edit is VIEWER-dependent — recompute from the held actor
    // (mirrors ability-section.ts). Shallow-copy so we don't mutate the shared casterCache object.
    const canEdit = typeof ftHasPlayerPermission === 'function' ? ftHasPlayerPermission(caster) : !!raw.can_edit;
    const hydrateList = (rows: RawSpellObj[] | undefined): SpellObj[] | undefined =>
        rows?.map((o) => hydrateCastableRow(o, caster));
    // summons_gain_caster_cantrips (Wizard v9): a summon lists its controller's cantrips (plain rows, no grant marker)
    const injectedCantrips = pageSummonInjectedCantrips(caster as unknown as Record<string, unknown>) as unknown as RawSpellObj[];
    const payload: HydratedPayload = {
        can_edit: canEdit,
        spells_known: hydrateList([...(raw.spells_known ?? []), ...injectedCantrips]),
        curses_known: hydrateList(raw.curses_known),
        at_will_spell: hydrateList(raw.at_will_spell),
        innate_spell_per_long_rest: hydrateList(raw.innate_spell_per_long_rest),
        charged_spell: hydrateList(raw.charged_spell),
    };

    const rootId = `ft_castable_${sanitizeId(casterId)}`;
    const searchId = `${rootId}_search`;
    const endConcId = `${rootId}_endconc`;
    // House spyglass/clear icons (pe-5 reserves room); wrapper carries .ft-inline-search-root so the
    // post-paint toggle below can find the glyph/clear spans. The actual row filtering still rides
    // the delegated `input` listener via data-ft-castable-root (filter-search-init.ts).
    const search = `<div class="col-12 col-lg-auto ms-lg-auto"><div class="position-relative ft-inline-search-root"><input type="search" id="${searchId}" class="form-control fate-tide-castable-spell-search pe-5" data-ft-castable-root="${rootId}" placeholder="Search Castable Spells" autocomplete="off">${SEARCH_TRAILING_ICONS}</div></div>`;
    // End Concentration toolbar (compact) sits just left of the search — painted post-innerHTML by the
    // info-panel island from window.FateTide.casterCache (empty when not concentrating, so the col collapses).
    // align-self-center + the flex row keep it vertically centered to the search input; gap-1 spaces
    // multiple concentration buttons (rendered WITHOUT the p-1 wrapper so they stay centered).
    const endConc = `<div class="col-auto d-flex align-items-center align-self-center gap-1 my-0 mx-2" id="${endConcId}"></div>`;
    // A summon that casts its controller's cantrips (a non-caster: max −1) lists them at level 0 — the Cantrips tier and its pill
    // (2026-10-09 rules smoke: the injected rows were built, then dropped by the summon's own −1 cap).
    const maxLevel = Math.max(injectedCantrips.length ? 0 : -1, Number(
        (caster?.computed?.creature?.spellcasting as { max_spell_level_castable?: number } | undefined)?.max_spell_level_castable
        ?? -1,
    ));
    const nav = `<div class="row g-2 align-items-center my-3 w-100 mx-0"><div class="col-12 col-lg" data-ft-spell-nav="${maxLevel}">${navPills(maxLevel, rootId)}</div>${endConc}${search}</div>`;

    const body = (buildInnateCharged(payload, caster) + buildLeveled(payload, caster, maxLevel))
        || EMPTY_CASTABLE_SPELLS;

    // An unchanged list keeps its painted, hydrated DOM (and whatever the player typed into its search); only the
    // concentration toolbar below, which reads statuses the markup does not carry, repaints every time.
    const renderEndConc = window.renderEndConcentrationButtonsInline;
    if (!paintCastableMarkup(container, `<div class="fate-tide-castable-spells-root ft-pc-spells" id="${rootId}" data-filter-mode="all" data-filter-level="">${nav}${body}</div>`)) {
        if (typeof renderEndConc === 'function') { try { renderEndConc(casterId, endConcId, false, true); } catch { /* non-fatal */ } }
        return;
    }

    // Hydrate rune faces + initialize the injected TomSelect dropdowns.
    window.ftMountRuneIcons?.(container);
    if (typeof initializeTomSelect === 'function') { try { initializeTomSelect(); } catch { /* noop */ } }

    // Spyglass <-> clear (X) swap + clear wiring (house pattern; catalog-list owns the same toggle).
    // The delegated input listener does the filtering — clearing dispatches a fresh `input` so the
    // rows re-show.
    const searchInput = document.getElementById(searchId) as HTMLInputElement | null;
    if (searchInput && !wiredSpellSearches.has(searchInput)) {
        wiredSpellSearches.add(searchInput);
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') as HTMLElement | null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') as HTMLElement | null;
        const syncTrailing = (): void => {
            const hasText = searchInput.value.trim().length > 0;
            glyph?.classList.toggle('d-none', hasText);
            clear?.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', syncTrailing);
        clear?.addEventListener('click', (e) => {
            e.preventDefault();
            searchInput.value = '';
            searchInput.focus();
            syncTrailing();
            searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        });
        syncTrailing();
    }

    // Paint the compact End Concentration toolbar (info-panel island, loaded alongside the sheet).
    if (typeof renderEndConc === 'function') {
        // withP1Wrapper=false: the col owns spacing (gap-1) + centering (align-items-center), so the
        // p-1 wrapper's vertical padding doesn't push the button off the search's centerline.
        try { renderEndConc(casterId, endConcId, false, true); } catch { /* non-fatal */ }
    }

    const root = document.getElementById(rootId);
    if (root) restoreFt3dSpellView(root);
}

/** Patch changed prepared-spell rows, preserving searches, other tiers and innate/charged controls. */
export function patchCastableSpellRows(caster: CachedCaster, casterId: string, container: HTMLElement, ids: readonly string[]): void {
    const root = container.querySelector<HTMLElement>('.ft-pc-spells');
    if (!root) return; // An unmounted pane will read the current cache on its first mount.
    const raw = sheetCastablePayload(caster) as unknown as SpellsPayload;
    const known = raw.spells_known ?? [], canEdit = typeof ftHasPlayerPermission === 'function' ? ftHasPlayerPermission(caster) : !!raw.can_edit;
    const template = document.createElement('template');
    for (const id of ids) {
        const old = root.querySelector<HTMLElement>(`.spells_known_div:not(.spells_known_div_curse) tr[data-ft-spell-id="${CSS.escape(id)}"]`);
        const spell = known.find(spell => (spell.item_id ?? spell.id)?.toUpperCase() === id.toUpperCase());
        if (old) {
            const section = old.closest('.spells_known_div');
            replaceHydratedHtml(old, ''); old.remove();
            if (!section?.querySelector('tbody tr')) section?.remove();
        }
        if (!spell) continue;
        const hydrated = hydrateCastableRow(spell, caster), level = hydrated.level;
        const html = leveledRow(hydrated, caster, canEdit);
        let section = root.querySelector<HTMLElement>(`.spells_known_div_${level}`);
        if (!section) {
            template.innerHTML = leveledTable(level === 0 ? 'Cantrips' : `${ordinal(level)} Level Spells`, `spells_known_div_${level}`, html, String(level));
            section = template.content.firstElementChild as HTMLElement;
            const after = [...root.querySelectorAll<HTMLElement>('[data-ft-spell-section-key]')]
                .find(el => /^\d+$/.test(el.dataset.ftSpellSectionKey ?? '') && Number(el.dataset.ftSpellSectionKey) > level);
            root.insertBefore(section, after ?? null);
        } else {
            template.innerHTML = `<table><tbody>${html}</tbody></table>`;
            const node = template.content.querySelector('tr')!;
            const after = known.slice(known.indexOf(spell) + 1).map(spell => section!.querySelector(`tr[data-ft-spell-id="${CSS.escape(spell.item_id ?? spell.id ?? '')}"]`)).find(Boolean);
            section.querySelector('tbody')!.insertBefore(node, after ?? null);
        }
        window.ftMountRuneIcons?.(section);
    }
    const empty = root.querySelector<HTMLElement>('[data-ft-castable-empty]');
    if (root.querySelector('tr[data-ft-spell-id]')) empty?.remove();
    else if (!empty) root.insertAdjacentHTML('beforeend', EMPTY_CASTABLE_SPELLS);
    const nav = root.querySelector<HTMLElement>('[data-ft-spell-nav]');
    const maxLevel = Number(caster.computed?.creature?.spellcasting?.max_spell_level_castable ?? -1);
    if (nav && nav.dataset.ftSpellNav !== String(maxLevel)) {
        replaceHydratedHtml(nav, navPills(maxLevel, root.id)); nav.dataset.ftSpellNav = String(maxLevel);
    }
    window.initializeTomSelect?.();
    restoreFt3dSpellView(root);
}

export async function renderCastableSpells(caster: CachedCaster, casterId: string, container: HTMLElement): Promise<void> {
    try {
        paintCastableSpells(sheetCastablePayload(caster) as unknown as SpellsPayload, caster, casterId, container);
    } catch (error) {
        container.innerHTML = displayRefusal(error);
    }
}
